import React, { ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ContainerLayout } from 'layouts';
import { FontBodyL, DigitsInput } from 'components/shared';
import replaceStarsWithBulletPoints from 'utils/replaceStarsWithBulletPoints/replaceStarsWithBulletPoints';
import { TranslationContext } from '@ds/react-utils';
import { useButtonsLoading, useInterval } from 'hooks';
import styled from '@emotion/styled';
import { apiService } from 'services';
import { routes } from 'static/routes';
import { setFocus } from 'utils/AccessibilityUtils';
import VerifyApplicationContext from 'contexts/VerifyApplicationContext';
import ResendModal from 'components/shared/ResendModal/ResendModal';
import { IPhoneNumber } from 'services/PhoneNumber';
import { sendCall, sendSMS } from 'queries/PhoneQueries';
import { ContinueModel } from 'types/ContinueModel';
import { PhoneVerificationResponseMessages } from 'types/PhoneVerificationResponseMessages';
import { AxiosError } from 'axios';

const PhoneNumber = styled.span`
    font-weight: bold;
`;

const ConfirmCodePage = (): ReactElement => {
    const context = useContext(VerifyApplicationContext);
    const { state } = useLocation<{ selectedPhoneNumber: IPhoneNumber; method: string }>();
    const history = useHistory();
    const translate = React.useContext(TranslationContext);
    const [code, setCode] = useState('');
    const [textBoxError, setTextBoxError] = useState('');

    const formRef = useRef<HTMLFormElement>(null);
    const [continueModel, setContinueModel] = useState<ContinueModel | undefined>(undefined);

    const countdownDuration = 5;
    const [countdown, setCountdown] = useState(countdownDuration);
    useInterval(() => {
        if (countdown > 0) {
            setCountdown(countdown - 1);
        }
    }, 1000);

    const { isPrimaryButtonLoading, setIsPrimaryButtonLoading, isSecondaryButtonLoading, areButtonsLoading } = useButtonsLoading();

    const resetCountdown = () => {
        setCountdown(countdownDuration);
    };

    const getContinueModel = () => {
        apiService.get<any>(
            '/App/GetClaimResponse',
            (response: ContinueModel) => {
                setContinueModel(response);
            },
            (error: AxiosError) => {
                if (error?.response?.status === 401) {
                    history.push(routes.SessionExpired);
                    return;
                }
                history.push(routes.ChooseMethod);
            }
        );
    };

    useEffect(() => {
        if (
            !!continueModel &&
            !!continueModel.callbackUri &&
            !!continueModel.claimResponse &&
            !!formRef &&
            !!formRef.current &&
            !!formRef.current.action
        ) {
            formRef.current.submit();
        }
    }, [continueModel, formRef]);

    useEffect(() => {
        setFocusOnCodeInput();
    }, []);

    const setFocusOnCodeInput = () => {
        setFocus('[data-focus="verify-confirm-code-verification-code-input"]');
    };

    const updateInput = (codes: string) => {
        setTextBoxError('');
        setCode(codes);
    };

    const onKeyDown = (key: string) => {
        if (key === 'Enter') {
            onConfirmCodeClick();
        }
    };

    const handleResponse = (response: PhoneVerificationResponseMessages) => {
        if (response.responseResult === 'Success') {
            getContinueModel();
        } else if (response.responseResult === 'Error') {
            if (response.responseMessage === 'VerificationCodeMisMatchError') {
                setTextBoxError(translate('Code is incorrect'));
                setFocusOnCodeInput();
                setIsPrimaryButtonLoading(false);
            } else if (response.responseMessage === 'MaxAttemptsExceeded') {
                history.push(routes.DocumentLocked);
            }
        }
    };

    const onConfirmCodeClick = useCallback(async () => {
        if (areButtonsLoading) {
            return;
        }

        if (code.length !== 6 || code.match(/[0-9]{6}/)?.length !== 1) {
            setTextBoxError(translate('Code must be 6 numbers'));
            setFocusOnCodeInput();
            return;
        }

        setIsPrimaryButtonLoading(true);

        apiService.post<any>(
            'phone/solve',
            {
                code: code
            },
            (response: PhoneVerificationResponseMessages) => {
                handleResponse(response);
            },
            onErrorCallback
        );
    }, [areButtonsLoading, setIsPrimaryButtonLoading, code, setTextBoxError, handleResponse, translate, history]);

    const onErrorCallback = (error: AxiosError) => {
        if (error?.response?.status === 401) {
            history.push(routes.SessionExpired);
        } else {
            history.push(routes.Error);
        }
    };

    return (
        <ContainerLayout>
            <ContainerLayout.Header>
                <ContainerLayout.Header.Title title={translate('Enter your code')} titleDataQa="verify-confirm-code-title" />
            </ContainerLayout.Header>

            <ContainerLayout.InnerContent>
                <FontBodyL data-qa="verify-confirm-code-intro">{translate('We sent a code to')}</FontBodyL>
                <FontBodyL data-qa="verify-confirm-code-phone-number">
                    <PhoneNumber>{replaceStarsWithBulletPoints(state.selectedPhoneNumber.phoneNumber ?? '')}</PhoneNumber>
                </FontBodyL>

                <DigitsInput
                    onChange={updateInput}
                    onKeyDown={onKeyDown}
                    label={translate('Verification Code')}
                    labelId="verify-confirm-code-verification-code-label"
                    describedBy="verify-confirm-code-verification-code-label"
                    description={translate('We sent a code to {PHONE_NUMBER}').replace('{PHONE_NUMBER}', state.selectedPhoneNumber.phoneNumber ?? '')}
                    numberOfDigits={6}
                    dataQa="verify-confirm-code-verification-code-input"
                    dataFocus="verify-confirm-code-verification-code-input"
                    autocomplete="one-time-code"
                    error={translate(textBoxError)}
                />

                <ResendModal
                    countdown={countdown}
                    resetCountdown={resetCountdown}
                    method={state.method}
                    displayChannel={context.displayChannel || ''}
                    onNewSms={() => {
                        sendSMS(
                            {
                                phoneNumber: state.selectedPhoneNumber.phoneNumber,
                                uniqueId: state.selectedPhoneNumber.uniqueId,
                                language: context.language
                            },
                            () => {},
                            onErrorCallback
                        );
                    }}
                    onNewCall={() => {
                        sendCall(
                            {
                                phoneNumber: state.selectedPhoneNumber.phoneNumber,
                                uniqueId: state.selectedPhoneNumber.uniqueId,
                                language: context.language
                            },
                            () => {},
                            onErrorCallback
                        );
                    }}
                />
            </ContainerLayout.InnerContent>

            <ContainerLayout.Footer>
                <ContainerLayout.Footer.Buttons>
                    <ContainerLayout.Footer.Buttons.Secondary
                        data-qa="verify-confirm-code-secondary-button"
                        text={translate('Back')}
                        onClick={history.goBack}
                        loading={isSecondaryButtonLoading}
                    />
                    <ContainerLayout.Footer.Buttons.Primary
                        data-qa="verify-confirm-code-primary-button"
                        text={translate('Confirm Code')}
                        onClick={onConfirmCodeClick}
                        loading={isPrimaryButtonLoading}
                    />
                </ContainerLayout.Footer.Buttons>
            </ContainerLayout.Footer>
            {/* FORM THAT WILL MAKE A POST BACK TO ACCOUNT SERVER AFTER THE CODE IS VERIFIED */}
            <form ref={formRef} method="POST" action={continueModel ? continueModel.callbackUri : ''} data-qa="verify-confirm-code-form">
                <input
                    type="hidden"
                    name={'claim_response'}
                    value={continueModel ? continueModel.claimResponse : ''}
                    data-qa="verify-confirm-code-form-input"
                ></input>
            </form>
        </ContainerLayout>
    );
};

export default ConfirmCodePage;
